/**
 * Used to select files to upload and show status while uploading.
 */

// Libraries
import { useState } from 'react'
import { useForm, useFieldArray, type SubmitHandler } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Table, Spinner } from 'react-bootstrap'
import { useRouteContext } from '@tanstack/react-router'

// Scripts
import { getSubmissionsQueryKey } from '@/queries.ts'
import { fetchAuthenticated } from '@/queries/shared'
import { compareFileNames, readFileContents } from '@/utils/files'
import { formatNumber } from '@/utils/numbers';

// Components
import Card from '@/components/shared/Card'
import LoaderButton from '@/components/shared/LoaderButton'
import TooltipTrigger from '@/components/shared/TooltipTrigger'
import BrowseFilesButton from '@/components/file-submission/UploadFilesCard/BrowseFilesButton'
import FullPageDropZone from '@/components/file-submission/UploadFilesCard/FullPageDropZone'
import { type RecordType, fileUploadRecordTypes, type FileEntry } from '@/models.ts';
import Icon from '@/components/shared/Icon';
import Alert from '@/components/shared/Alert';

interface FileResponse extends FileEntry {
    status?: 'Uploading' | 'Uploaded' | 'Failed';
    error?: string;
}

type UploadData = {
    files: FileEntry[];
}

type ValidatedFiles = {
    invalidTypeFiles: File[];
    duplicateNameFiles: File[];
    validFiles: File[];
}

const LINE_HEIGHT = '2.125rem';

const fileTypes = ['text/csv'];
const fileExtensions = ['.csv'];
const fileTypesLabel = ".csv";
// TO-DO: Add support for Excel:
// const fileMIMETypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
// const fileExtensions = ['.csv', '.xls', '.xlsx'];
// const fileTypesLabel = ".csv, .xls, .xlsx";

// Static functions

/**
 * Returns true if the file is an accepted type.
 * Checks the file extension OR the browser-reported MIME type, accepting the file
 * if either matches. Extension is the reliable signal: browsers (notably Firefox)
 * often report an empty or unexpected MIME type for .csv files depending on the
 * machine's file-type registry, so we cannot depend on MIME alone.
 */
function isAcceptedType(
    file: File,
    acceptedTypes?: string[],
    acceptedExtensions?: string[]
): boolean {
    const extensionMatches = acceptedExtensions?.some(ext =>
        file.name.toLowerCase().endsWith(ext.toLowerCase())
    ) ?? false;
    const mimeMatches = acceptedTypes?.includes(file.type) ?? false;
    return extensionMatches || mimeMatches;
}

function validateFiles(
    newFiles: File[],
    existingFiles: File[],
    acceptedTypes?: string[],
    acceptedExtensions?: string[]
): ValidatedFiles {
    const existingNames = new Set(existingFiles.map(f => f.name));

    // Only enforce a type restriction if one was actually provided.
    const hasTypeRestriction =
        (acceptedTypes != null && acceptedTypes.length > 0) ||
        (acceptedExtensions != null && acceptedExtensions.length > 0);

    return newFiles.reduce((acc, file) => {
        if (hasTypeRestriction && !isAcceptedType(file, acceptedTypes, acceptedExtensions)) {
            acc.invalidTypeFiles.push(file);
        } else if (existingNames.has(file.name)) {
            acc.duplicateNameFiles.push(file);
        } else {
            acc.validFiles.push(file);
        }
        return acc;
    }, {
        invalidTypeFiles: [],
        duplicateNameFiles: [],
        validFiles: []
    } as ValidatedFiles);
}

/** Extracts the first column value from a CSV regex match */
function extractFirstColumn(match: RegExpExecArray | null): string {
    if (!match) return '';
    return match[2] ? match[2].replace(/""/g, '"') : match[3] ?? '';
}

const autoSelectRecordTypes = async (entries: FileEntry[]): Promise<FileEntry[]> => {
    // Auto-select record type based on record column count
    return await Promise.all(
        entries.map(async entry => {
            const { recordType, isHeaderRow } = await getDefaultRecordType(entry.file);
            return {
                ...entry,
                recordType,
                isHeaderRow,
            };
        })
    );
}

const getDefaultRecordType = async (recordFile: File): Promise<{ recordType: RecordType | '', isHeaderRow: boolean }> => {
    // Based off of https://www.bennadel.com/blog/1504-ask-ben-parsing-csv-strings-with-javascript-exec-regular-expression-command.htm
    // Create a regular expression to parse the CSV values.
    const objPattern = new RegExp(
        (
            // Delimiters.
            "(\\,|\\r?\\n|\\r|^)" +

            // Quoted fields.
            "(?:\"([^\"]*(?:\"\"[^\"]*)*)\"|" +

            // Standard fields.
            "([^\"\\,\\r\\n]*))"
        ),
        "gi"
    );

    const fileContent = await readFileContents(recordFile);
    // Get first CSV value from file
    const column1Value = extractFirstColumn(objPattern.exec(fileContent));
    const firstRowMatch = fileUploadRecordTypes.find(x => x === column1Value) ?? '';
    if (firstRowMatch) return { recordType: firstRowMatch as RecordType, isHeaderRow: false };

    // else first Row not match go to second row to get header
    const lines = fileContent.split(/\r?\n/);
    if (lines.length > 1) {
        objPattern.lastIndex = 0;    // VERY IMPORTANT - reset regex pointer
        const secondRowColumn1 = extractFirstColumn(objPattern.exec(lines[1]));
        const secondRowMatch = fileUploadRecordTypes.find(x => x === secondRowColumn1);
        if (secondRowMatch) return { recordType: secondRowMatch as RecordType, isHeaderRow: true };
    }

    return { recordType: '', isHeaderRow: false };
}
// Component
export default function FileUploadTable({ className }: { className?: string }) {
    const queryClient = useQueryClient();
    const leaContext = useRouteContext({ from: '/$leaid' })
    const esiidPath = leaContext.currentUserOrg.esiIdPath;
    const editPermissions = leaContext.editFilePermissions()

    const [ fileEntries, setFileEntries ] = useState<FileResponse[]>([]);
    const [ invalidTypeFiles, setInvalidTypeFiles ] = useState<File[]>([]);
    const [ duplicateNameFiles, setDuplicateNameFiles ] = useState<File[]>([]);

    const fileCount = fileEntries.length;
    const failCount = fileEntries.filter(f => f.status === 'Failed').length;

    const { 
        control, 
        handleSubmit, 
        register, 
        getValues, 
        formState: { isDirty, isValid, isSubmitting } 
    } = useForm<UploadData>({
        defaultValues: {
            files: [],
        },
    });

    const isSubmitBlocked = getValues("files").some(entry => !entry.recordType) || !(isDirty && isValid);

    const { fields, remove, replace } = useFieldArray({
        name: 'files',
        control,
    });

    const handleFilesDrop = (newFiles: File[]) => {
        setInvalidTypeFiles([])
        setDuplicateNameFiles([])

        const {
            invalidTypeFiles,
            duplicateNameFiles,
            validFiles
        } = validateFiles(newFiles, getValues('files').map(f => f.file), fileTypes, fileExtensions);
        if (invalidTypeFiles.length > 0) {
            setInvalidTypeFiles(invalidTypeFiles);
        }
        if (duplicateNameFiles.length > 0) {
            setDuplicateNameFiles(duplicateNameFiles);
        }
        if (validFiles.length > 0) {
            addValidFiles(validFiles).catch((error) => { throw error; });
        }
    };

    // Initialize field array when files are dropped
    const addValidFiles = async (incomingFiles: File[]) => {
        if (incomingFiles.length === 0) return;

        const existingNames = new Set(fileEntries.map(f => f.file.name));

        let newEntries: FileEntry[] = incomingFiles
            .filter((file) => !existingNames.has(file.name))
            .map((file) => ({
                file,
                recordType: '',
                isHeaderRow: false,
            }));

        if (newEntries.length === 0) return [];

        newEntries = await autoSelectRecordTypes(newEntries);

        // Get existing file entries from local state and react hook form 
        // to sort in new entries while preserving recordType, isHeaderRow, and status values
        const existingEntries = getValues("files").map(field => {
            return {
                file: field.file,
                recordType: field.recordType,
                isHeaderRow: field.isHeaderRow ?? false,
            }
        });
        const combinedEntries: FileResponse[] = [...existingEntries, ...newEntries]
            .sort((a, b) => a.file.name.localeCompare(b.file.name));

        // Update react hook form
        replace(combinedEntries);

        // Update local state
        setFileEntries(combinedEntries);
    }

    const removeFile = (index: number) => {
        if (index < 0 || index >= fileEntries.length) return;
        
        // Remove from react hook form
        remove(index); 
        
        // Update local state
        setFileEntries((prev) => prev.filter((_, i) => i !== index)); 
    }
    
    const uploadMutation = useMutation({
        mutationFn: async ({file, recordType, isHeaderRow}: FileEntry) => {
            const formData = new FormData();
            formData.append('csvFile', file);
            formData.append('recordType', recordType);
            formData.append('isHeaderRow', String(isHeaderRow ?? false));

            return await fetchAuthenticated(leaContext, `/${leaContext.currentUserOrg.esiIdPath}/files/ajax/UploadRecordsAjax`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                },
                body: formData,
            })
        },
    });

    const handleSingleFileSubmit = async (entry: FileEntry, index: number ) => {
        setFileEntries((prev) => 
            prev.map((f) => entry.file.name === f.file.name ? {...entry, status: 'Uploading'} : f)
        );

        try {
            await uploadMutation.mutateAsync({ file: entry.file, recordType: entry.recordType, isHeaderRow: entry.isHeaderRow });
            await queryClient.invalidateQueries({ queryKey: getSubmissionsQueryKey(esiidPath) });

            // Remove from react hook form
            remove(index);

            // Remove from local state
            setFileEntries((prev) => 
                prev.filter(f => entry.file.name !== f.file.name)
            );
        } catch (error) {
            setFileEntries((prev) => 
                prev.map((f) => 
                    entry.file.name === f.file.name ? {
                        ...entry, 
                        status: 'Failed', 
                        error: (error as Error).message
                    } : f
                )
            );
        }
    }

    const onSubmit: SubmitHandler<UploadData> = async (data: UploadData) => {
        setInvalidTypeFiles([])
        setDuplicateNameFiles([])
        
        const files = data.files;
        const {
            classroomFiles,
            staffFiles,
            childFiles,
            otherFiles
        }: {
            classroomFiles: FileEntry[],
            staffFiles: FileEntry[],
            childFiles: FileEntry[],
            otherFiles: FileEntry[]
        } = files.reduce((acc, fileEntry) => {
            switch (fileEntry.recordType) {
                case 'CLRM':
                    acc.classroomFiles.push(fileEntry);
                    break;
                case 'STFF':
                    acc.staffFiles.push(fileEntry);
                    break;
                case 'CHLD':
                    acc.childFiles.push(fileEntry);
                    break;
                default:
                    acc.otherFiles.push(fileEntry);
            }
            return acc;
        }, {
            classroomFiles: [] as FileEntry[],
            staffFiles: [] as FileEntry[],
            childFiles: [] as FileEntry[],
            otherFiles: [] as FileEntry[]
        });

        // Submit CHLD files first so they will be queued first
        await Promise.all(classroomFiles.map(async (entry, index) => await handleSingleFileSubmit(entry, index)))
        await Promise.all(staffFiles.map(async (entry, index) => await handleSingleFileSubmit(entry, index)))
        await Promise.all(childFiles.map(async (entry, index) => await handleSingleFileSubmit(entry, index)))
        await Promise.all(otherFiles.map(async (entry, index) => await handleSingleFileSubmit(entry, index)))
        
        const remainingFiles = fileEntries.filter(f => f.status === 'Failed');
        replace(remainingFiles.map(({ file, recordType }) => ({ file, recordType })));
        setFileEntries(remainingFiles);
    }

    return (<>
        <Card className={className}>
            { !isSubmitting && fileEntries.some(f => f.isHeaderRow) &&
                <div style={{ margin: '-1.5rem -1.5rem 0 -1.5rem' }}>
                    <Alert variant="info" dismissible className="mb-0 rounded-0">
                        <p className="mb-0">The following {fileEntries.filter(f => f.isHeaderRow).length === 1 ? 'file contains a header row' : 'files contain a header row'} that will be skipped during processing:</p>
                        <ul className="mb-0">
                            {fileEntries.filter(f => f.isHeaderRow).map(entry =>
                                <li key={entry.file.name}>{entry.file.name}</li>
                            )}
                        </ul>
                    </Alert>
                </div>
            }

            <div className="d-flex align-items-start justify-content-between mb-1 mt-3">
                <div>
                    <div className="fs-5 fw-bold">Selected Files</div>
                    <div style={{ height: "1.5rem" }}>
                        {!isSubmitting && fields.length > 0 &&
                            <span>{fields.length} files selected</span>
                        }
                        { isSubmitting &&
                            <span role="alert">Uploading {fileCount} files</span>
                        }
                    </div>
                </div>
                <BrowseFilesButton 
                    onSelect={handleFilesDrop}
                    fileTypes={fileTypes}
                    acceptExtensions={fileExtensions}
                    multiple={true}
                />
            </div>

            {invalidTypeFiles.length > 0 && (
                <Alert 
                    variant="danger" 
                    dismissible
                    onClose={() => setInvalidTypeFiles([])}
                >
                    <p>Unable to add the following files. Only .csv files are allowed:</p>
                    <ul>
                        {invalidTypeFiles
                            .sort(compareFileNames)
                            .map((file) => (
                                <li key={file.name}>{file.name}</li>
                        ))}
                    </ul>
                </Alert>
            )}

            {duplicateNameFiles.length > 0 && (
                <Alert 
                    variant="danger" 
                    dismissible
                    onClose={() => setDuplicateNameFiles([])}
                >
                    <p>Unable to add the following files. A file of the same name is already added to this batch:</p>
                    <ul>
                        {duplicateNameFiles
                            .sort(compareFileNames)
                            .map((file) => (
                                <li key={file.name}>{file.name}</li>
                        ))}
                    </ul>
                </Alert>
            )}

            { !isSubmitting && failCount > 0 &&
                <Alert variant="danger" dismissible className="mb-3">
                    <p>The following {failCount} uploads failed:</p>
                    <ul>
                        {fileEntries.filter(f => f.status === 'Failed').map(entry =>
                            <li key={`${entry.file.name} ${entry.file.lastModified}`}>
                                {entry.file.name} - {entry.error || 'Unknown error'}
                            </li>
                        )}
                    </ul>
                </Alert>
            }

            <form onSubmit={handleSubmit(onSubmit)}>
                <Table>
                    <caption className="visually-hidden">Selected files</caption>
                    <thead>
                        <tr>
                            {/* <th></th> */}
                            <th className="w-50">Filename</th>
                            <th className="w-50">Record Type</th>
                            <th className="text-nowrap">File Size</th>
                            <th scope="col" style={{ minWidth: "4rem" }}>
                                <span className="visually-hidden">Actions</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {fields.length > 0 && fields.map((field, index) => {
                            const fileEntry = fileEntries.find(f => f.file.name === field.file.name);
                            return (
                                <tr key={field.id} style={{ lineHeight: LINE_HEIGHT }}>
                                    {/* <td className="align-middle">{index + 1}</td> */}
                                    <td className="w-50 align-middle">{field.file.name}</td>
                                    <td className="w-75 align-middle">
                                        <div style={{maxWidth: '300px'}}>
                                            {isSubmitting ? 
                                                // For some reason doesn't update reactively unless use getValues
                                                getValues('files')[index].recordType
                                            :
                                                <select
                                                    className="form-select"
                                                    defaultValue="" 
                                                    aria-label={`Record type for ${field.file.name}`}
                                                    disabled={isSubmitting}
                                                    {...register(`files.${index}.recordType`, { required: "Record type is required" })}
                                                >
                                                    <option value="">Select option</option>
                                                    {Object.entries(leaContext.recordTypeLabels).filter(([type, label]) => editPermissions.find(p => p === type)).map(([type, label]) => 
                                                        <option key={type} value={type}>{label}</option>
                                                    )}
                                                </select>
                                            }
                                        </div>
                                    </td>
                                    <td className="text-end align-middle text-nowrap">{formatNumber(field.file.size/1000, 1)} KB</td>
                                    
                                    {/* Remove button + Status column */}
                                    <td 
                                        style={{ minWidth: '4rem'}} 
                                        className="text-end align-middle"
                                    >
                                        <div className="d-flex align-items-center">
                                            {fileEntry?.status &&
                                                <>
                                                    {fileEntry.status === 'Uploading' ?
                                                        <TooltipTrigger tooltipText="Uploading">
                                                            <Spinner variant="secondary" className="me-2" animation="border" size="sm" role="status" aria-hidden="true" />
                                                        </TooltipTrigger>
                                                    : fileEntry.status === 'Failed' ?
                                                        <TooltipTrigger tooltipText="Failed upload">
                                                            <Icon name="error" className="text-danger me-2" />
                                                        </TooltipTrigger>
                                                    : null}
                                                </> 
                                            }
                                            {!isSubmitting &&
                                                <TooltipTrigger tooltipText="Remove file">
                                                    <Button 
                                                        variant="light"
                                                        onClick={() => removeFile(index)}
                                                        aria-label="Remove file"
                                                    >
                                                        <Icon name="trash" />
                                                    </Button>
                                                </TooltipTrigger>
                                            }
                                        </div>
                                    </td>
                                </tr>
                            )
                        })}

                        {fields.length === 0 && 
                            <tr>
                                <td colSpan={5} className="text-center py-5">
                                    <p className="mb-0">There are no files selected.</p>
                                    <p className="mb-0">Drag & drop files anywhere in window or click the "Browse Files" button to select files.</p>
                                    <p className="mb-0">Accepted file types: {fileTypesLabel}</p>
                                </td>
                            </tr>
                        }
                    </tbody>
                </Table>
                <div className="text-right">
                    <LoaderButton
                        variant="primary"
                        type="submit"
                        disabled={isSubmitting}
                        aria-disabled={isSubmitBlocked}
                        style={isSubmitBlocked ? { opacity: 0.65 } : undefined}
                        onClick={(e) => { if (isSubmitBlocked) e.preventDefault(); }}
                        loading={isSubmitting}
                    >Submit Files</LoaderButton>
                </div>
            </form>
        </Card>
        
        <FullPageDropZone onDropFiles={handleFilesDrop} />
    </>)
}